import React, { useState, useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import FilterListIcon from '@mui/icons-material/FilterList'
import PublishedWithChangesOutlinedIcon from '@mui/icons-material/PublishedWithChangesOutlined'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import AppModal from '../../../components/AppModal'
import AppTable from '../../../shared/AppTable'
import AppButton from '../../../shared/AppButton'
import CustomFilterInput from '../../../shared/CustomFilterInput'
import TableSkeletonLoader from '../../../common/TableLoading'
import Pagination from '../../../components/Pagination'
import claimsGQL from '../../../gql/claims'
import { API_BASE_URL } from '../../../config/apiConfig'

export default function ClaimItemIssueCodesModal({
  open,
  onClose,
  initialFromDate = '',
  initialToDate = '',
  initialErrorCode = 'IB-00238'
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [fromDate, setFromDate] = useState(initialFromDate)
  const [toDate, setToDate] = useState(initialToDate)
  const [errorCode, setErrorCode] = useState(initialErrorCode)
  const [exporting, setExporting] = useState(false)
  const [page, setPage] = useState(1)

  const [fetchIssueCodes, { loading, data }] = useLazyQuery(
    claimsGQL.GET_CLAIM_ITEM_ISSUE_CODES,
    {
      fetchPolicy: 'network-only'
    }
  )

  useEffect(() => {
    if (open) {
      setFromDate(initialFromDate)
      setToDate(initialToDate)
      setErrorCode(initialErrorCode || 'IB-00238')
      setPage(1)
      loadCodes(initialFromDate, initialToDate, initialErrorCode || 'IB-00238', 1)
    }
  }, [open, initialFromDate, initialToDate, initialErrorCode])

  const loadCodes = (fDate, tDate, errCode, currentPage = 1) => {
    fetchIssueCodes({
      variables: {
        input: {
          from_date: fDate,
          to_date: tDate,
          error_value: errCode,
          limit: 50,
          page: currentPage
        }
      }
    })
  }

  const handleApplyFilter = () => {
    setPage(1)
    loadCodes(fromDate, toDate, errorCode, 1)
  }

  const handlePageChange = newPage => {
    setPage(newPage)
    loadCodes(fromDate, toDate, errorCode, newPage)
  }

  const handleExportExcel = async () => {
    try {
      setExporting(true)
      const token = localStorage.getItem('token')?.replaceAll('"', '')
      const response = await axios.get(
        `${API_BASE_URL}/claim/item/bulk-replace/issues/export`,
        {
          params: {
            from_date: fromDate,
            to_date: toDate,
            error_code: errorCode
          },
          responseType: 'blob',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      )
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `Claim_Item_Issues_${errorCode || 'IB-00238'}_${fromDate || 'all'}_to_${toDate || 'all'}.xlsx`
      )
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      toast.success(t('Excel file downloaded successfully'))
    } catch (err) {
      console.error(err)
      toast.error(t('Failed to export Excel file'))
    } finally {
      setExporting(false)
    }
  }

  const handleUseInBulkReplace = () => {
    onClose()
    navigate('/claims/item-bulk-replace/replace')
  }

  const resultData = data?.getClaimItemIssueCodes?.data || []
  const totalRecords = data?.getClaimItemIssueCodes?.total || 0
  const totalPages = Math.ceil(totalRecords / 50) || 1

  const columns = [
    {
      header: t('SQ #'),
      align: 'center',
      render: (row, index) => (
        <span className='font-semibold text-slate-600 text-xs'>
          {row.sequence_no ?? (page - 1) * 50 + index + 1}
        </span>
      )
    },
    // {
    //   header: t('Error Code'),
    //   align: 'center',
    //   render: row => (
    //     <span className='text-xs font-semibold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200'>
    //       {row.error_code || errorCode || 'IB-00238'}
    //     </span>
    //   )
    // },
    {
      header: t('Standard Code'),
      render: row => (
        <div className='flex flex-col'>
          <span className='font-semibold text-slate-800 text-xs'>
            {row.standard_code || '---'}
          </span>
          <span className='text-[11px] text-slate-500 max-w-[200px] truncate' title={row.standard_description}>
            {row.standard_description || '---'}
          </span>
        </div>
      )
    },
    {
      header: t('Non Standard Code'),
      render: row => (
        <div className='flex flex-col'>
          <span className='font-semibold text-slate-800 text-xs'>
            {row.non_standard_code || '---'}
          </span>
          <span className='text-[11px] text-slate-500 max-w-[200px] truncate' title={row.non_standard_description}>
            {row.non_standard_description || '---'}
          </span>
        </div>
      )
    },
    {
      header: t('Occurrence Count'),
      align: 'center',
      render: row => (
        <span className='font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-full text-xs'>
          {row.occurrence_count ?? 0}
        </span>
      )
    }
  ]

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={t('Claim Item Issue Codes Summary')}
      maxWidth='lg'
    >
      <div className='flex flex-col gap-3'>
        <div className='flex flex-wrap items-end gap-2 bg-slate-50/80 px-3 pt-2 pb-3 rounded-md border border-slate-200 justify-between shadow-none'>
          <div className='flex flex-wrap items-end gap-2'>
            <div className='w-36'>
              <CustomFilterInput
                label={t('From Date')}
                type='date'
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
              />
            </div>
            <div className='w-36'>
              <CustomFilterInput
                label={t('To Date')}
                type='date'
                value={toDate}
                onChange={e => setToDate(e.target.value)}
              />
            </div>
            <AppButton
              label={t('Filter')}
              onClick={handleApplyFilter}
              startIcon={<FilterListIcon style={{ fontSize: 16 }} />}
              className='!h-[28px] !text-[12px]'
            />
          </div>

          <div className='flex items-center gap-2'>
            <AppButton
              label={exporting ? t('Exporting...') : t('Download Excel')}
              variant='outlined'
              onClick={handleExportExcel}
              disabled={exporting}
              startIcon={<FileDownloadOutlinedIcon style={{ fontSize: 16 }} />}
              className='!border-emerald-600 !text-emerald-700 hover:!bg-emerald-50 !h-[28px] !text-[12px]'
            />
            {/* <AppButton
              label={t('Use In Bulk Replace')}
              variant='outlined'
              onClick={handleUseInBulkReplace}
              startIcon={<PublishedWithChangesOutlinedIcon style={{ fontSize: 16 }} />}
              className='!border-primary !text-primary hover:!bg-primary/10 !h-[28px] !text-[12px]'
            /> */}
          </div>
        </div>

        <div className='min-h-fit bg-white rounded-md border border-slate-200 overflow-hidden'>
          <AppTable
            columns={columns}
            data={resultData}
            loading={loading}
            actions={false}
            bodyRowPadding='8px 12px'
            LoaderComponent={
              <TableSkeletonLoader columnCount={columns.length} rowCount={5} />
            }
          />
        </div>

        {totalRecords > 0 && (
          <div className='mt-0.5'>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </AppModal>
  )
}
